/**
 * Parámetros de búsqueda de la ruta `/disponibilidad`.
 *
 * Viven en su propio módulo porque los necesitan dos archivos que no se pueden
 * importar entre sí: `enrutador.tsx`, que declara la ruta y valida la URL, y
 * `rutas/Disponibilidad.tsx`, que es el componente que lee esos parámetros.
 *
 * La fecha y el espacio van en la URL a propósito: así la vista se puede
 * recargar, guardar y compartir con otra persona sin perder lo que se estaba
 * mirando.
 */

export interface BusquedaDisponibilidad {
  /** Fecha de calendario `YYYY-MM-DD`. Siempre presente. */
  fecha: string
  /**
   * Clave del espacio, por ejemplo `CUB-01`. Ausente significa «todos los
   * espacios», y por eso se omite de la URL en vez de viajar como cadena vacía.
   */
  espacio?: string | undefined
}

const PATRON_FECHA = /^\d{4}-\d{2}-\d{2}$/

/**
 * `true` solo si el texto es una fecha de calendario que existe. El patrón no
 * basta: `2027-02-30` lo cumple y no es un día real.
 */
export function esFechaDeCalendario(valor: string): boolean {
  return fechaDeCalendarioAInstante(valor) !== null
}

/**
 * Convierte `YYYY-MM-DD` al mediodía UTC de ese día, o `null` si no es una
 * fecha real.
 *
 * Es una fecha de calendario, no un instante: no representa un momento en
 * ninguna zona. Se ancla en UTC para que al darle formato no se corra de día
 * en ningún sentido.
 */
export function fechaDeCalendarioAInstante(valor: string): Date | null {
  if (!PATRON_FECHA.test(valor)) return null

  const partes = valor.split('-')
  const anio = Number(partes[0])
  const mes = Number(partes[1])
  const dia = Number(partes[2])

  const instante = new Date(Date.UTC(anio, mes - 1, dia, 12))
  if (
    instante.getUTCFullYear() !== anio ||
    instante.getUTCMonth() !== mes - 1 ||
    instante.getUTCDate() !== dia
  ) {
    return null
  }
  return instante
}

/**
 * Fecha de hoy según el reloj de esta máquina.
 *
 * HUECO DECLARADO: la zona horaria del sistema viaja en la respuesta del
 * backend (`zona_horaria`), pero este valor se necesita ANTES de la primera
 * respuesta, para armar la primera petición. Por eso, y solo para el valor
 * inicial del selector, se usa el calendario local del navegador. Todas las
 * horas que se muestran sí se convierten con la zona que reporta el backend.
 */
export function fechaDeHoy(): string {
  const ahora = new Date()
  const anio = String(ahora.getFullYear()).padStart(4, '0')
  const mes = String(ahora.getMonth() + 1).padStart(2, '0')
  const dia = String(ahora.getDate()).padStart(2, '0')
  return `${anio}-${mes}-${dia}`
}

/**
 * Valida los parámetros que vienen en la URL.
 *
 * Una fecha ausente se rellena con hoy; una fecha presente pero malformada se
 * conserva tal cual, para que la pantalla pueda decir que esa fecha no sirve en
 * vez de corregirla en silencio y mostrar un día que nadie pidió.
 */
export function validarBusquedaDisponibilidad(
  entrada: Record<string, unknown>,
): BusquedaDisponibilidad {
  const fecha = entrada['fecha']
  const espacio = entrada['espacio']

  return {
    fecha: typeof fecha === 'string' && fecha !== '' ? fecha : fechaDeHoy(),
    espacio:
      typeof espacio === 'string' && espacio.trim() !== ''
        ? espacio.trim()
        : undefined,
  }
}
